import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun } from '@fortawesome/pro-solid-svg-icons/faSun';
import { faMoon } from '@fortawesome/pro-solid-svg-icons/faMoon';
import { faSquare } from '@fortawesome/pro-regular-svg-icons/faSquare';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { Checkbox } from 'us-react-components/dist/Checkbox/Checkbox';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Button, ButtonType } from '../../../../components/Button';
import {
  AvailabilityStatusService,
  JobAvailabilityType,
  JobPreference,
} from 'us-web-services';
import toast from 'react-hot-toast';
import { ModalBase } from './types';

// -- Availability Modal (job seeking status) -------------------------------
const STATUS_DESCRIPTION_MAX = 500;

interface RecurringDay {
  dayOfWeek?: { code?: string };
  morning?: boolean;
  afternoon?: boolean;
  night?: boolean;
}

const DEFAULT_RECURRING_DAYS: RecurringDay[] = [
  { dayOfWeek: { code: 'SUN' }, morning: false, afternoon: false, night: false },
  { dayOfWeek: { code: 'MON' }, morning: false, afternoon: false, night: false },
  { dayOfWeek: { code: 'TUE' }, morning: false, afternoon: false, night: false },
  { dayOfWeek: { code: 'WED' }, morning: false, afternoon: false, night: false },
  { dayOfWeek: { code: 'THU' }, morning: false, afternoon: false, night: false },
  { dayOfWeek: { code: 'FRI' }, morning: false, afternoon: false, night: false },
  { dayOfWeek: { code: 'SAT' }, morning: false, afternoon: false, night: false },
];

type RecurringSlot = 'morning' | 'afternoon' | 'night';

const AvailabilityRecurringEditor = ({
  days,
  onToggle,
}: {
  days: RecurringDay[];
  onToggle: (index: number, slot: RecurringSlot) => void;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const cellButton = (
    on: boolean,
    dayPart: 'sun' | 'moon',
    dayCode: string,
    slot: RecurringSlot,
    index: number,
  ): JSX.Element => (
    <button
      type='button'
      aria-label={t('pages.sitterProfile.modals.jobSeekingStatusModal.toggleCellAriaLabel', {
        slot,
        day: dayCode,
      })}
      onClick={(): void => onToggle(index, slot)}
      className='bg-transparent border-0 p-0 cursor-pointer'
    >
      <FontAwesomeIcon
        icon={on ? (dayPart === 'sun' ? faSun : faMoon) : faSquare}
        className={
          on
            ? dayPart === 'sun'
              ? 'text-yellow-500'
              : 'text-sapphire'
            : 'text-neutral-300'
        }
      />
    </button>
  );
  return (
    <div className='inline-block mx-auto'>
      <div className='grid grid-cols-[1.5rem_repeat(7,1.75rem)] items-center gap-x-1 gap-y-2'>
        <div />
        {days.map((day, i) => (
          <div key={`h-${i}`} className='text-center text-[10px] font-semibold'>
            {day.dayOfWeek?.code?.charAt(0) ?? ''}
          </div>
        ))}
        <div className='text-[10px] font-semibold'>
          {t('pages.sitterProfile.modals.jobSeekingStatusModal.amLabel')}
        </div>
        {days.map((day, i) => (
          <div key={`am-${i}`} className='text-center'>
            {cellButton(!!day.morning, 'sun', day.dayOfWeek?.code ?? '', 'morning', i)}
          </div>
        ))}
        <div className='text-[10px] font-semibold'>
          {t('pages.sitterProfile.modals.jobSeekingStatusModal.pmLabel')}
        </div>
        {days.map((day, i) => (
          <div key={`pm-${i}`} className='text-center'>
            {cellButton(!!day.afternoon, 'sun', day.dayOfWeek?.code ?? '', 'afternoon', i)}
          </div>
        ))}
        <div />
        {days.map((day, i) => (
          <div key={`n-${i}`} className='text-center'>
            {cellButton(!!day.night, 'moon', day.dayOfWeek?.code ?? '', 'night', i)}
          </div>
        ))}
      </div>
    </div>
  );
};

export interface AvailabilityModalSitter {
  id?: number;
  availabilityStatus?: {
    id?: number;
    description?: string;
    interviewTimes?: string;
    jobPreferences?: Array<{ type?: string }>;
    recurringAvailability?: Array<unknown>;
  };
}

export const AvailabilityModal = ({
  isOpen,
  onClose,
  sitter,
  canEdit,
  onSaved,
  lastCalendarUpdateLabel,
}: ModalBase & {
  sitter: AvailabilityModalSitter;
  canEdit: boolean;
  onSaved?: () => void | Promise<void>;
  lastCalendarUpdateLabel?: string;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const initialPrefs = sitter.availabilityStatus?.jobPreferences ?? [];
  const hasType = (type: JobAvailabilityType): boolean =>
    initialPrefs.some((p) => p.type === type);

  const initialRecurring = (): RecurringDay[] => {
    const existing = (sitter.availabilityStatus?.recurringAvailability ?? []) as RecurringDay[];
    if (existing.length === DEFAULT_RECURRING_DAYS.length) return existing.map((d) => ({ ...d }));
    return DEFAULT_RECURRING_DAYS.map((d) => ({ ...d, dayOfWeek: { ...d.dayOfWeek } }));
  };

  const [occasional, setOccasional] = useState(hasType(JobAvailabilityType.occasional));
  const [partTime, setPartTime] = useState(hasType(JobAvailabilityType.partTime));
  const [fullTime, setFullTime] = useState(hasType(JobAvailabilityType.fullTime));
  const [description, setDescription] = useState(
    sitter.availabilityStatus?.description ?? '',
  );
  const [interviewTimes, setInterviewTimes] = useState(
    sitter.availabilityStatus?.interviewTimes ?? '',
  );
  const [recurringAvailability, setRecurringAvailability] =
    useState<RecurringDay[]>(initialRecurring);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setOccasional(hasType(JobAvailabilityType.occasional));
      setPartTime(hasType(JobAvailabilityType.partTime));
      setFullTime(hasType(JobAvailabilityType.fullTime));
      setDescription(sitter.availabilityStatus?.description ?? '');
      setInterviewTimes(sitter.availabilityStatus?.interviewTimes ?? '');
      setRecurringAvailability(initialRecurring());
    }

  }, [isOpen, sitter.id]);

  const toggleRecurring = (index: number, slot: RecurringSlot): void => {
    setRecurringAvailability((prev) =>
      prev.map((d, i) => (i === index ? { ...d, [slot]: !d[slot] } : d)),
    );
  };

  const save = async (): Promise<void> => {
    if (!sitter.id) return;
    setSaving(true);
    const prefs: JobPreference[] = [];
    if (occasional) prefs.push({ type: JobAvailabilityType.occasional });
    if (partTime) prefs.push({ type: JobAvailabilityType.partTime });
    if (fullTime) prefs.push({ type: JobAvailabilityType.fullTime });
    const payload = {
      sitterId: sitter.id,
      jobPreferences: prefs,
      description,
      interviewTimes,
      recurringAvailability,
    };
    try {
      const statusId = sitter.availabilityStatus?.id;
      if (statusId) {
        await AvailabilityStatusService.patch(statusId, { id: statusId, ...payload });
      } else {
        await AvailabilityStatusService.create(payload);
      }
      toast.success(t('pages.sitterProfile.availability.updated'));
      await onSaved?.();
      onClose();
    } catch {
      toast.error(t('pages.sitterProfile.toasts.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t('pages.sitterProfile.modals.availability')}
        className='w-full max-w-2xl'
      >
        {sitter.availabilityStatus?.description ? (
          <p className='mb-3'>{sitter.availabilityStatus.description}</p>
        ) : (
          <p className='mb-3 text-neutral-500'>
            {t('pages.sitterProfile.modals.jobSeekingStatusModal.noAvailabilityPosted')}
          </p>
        )}
        <p className='text-sm text-neutral-600'>
          {t('pages.sitterProfile.modals.jobSeekingStatusModal.fullScheduleLeading')}{' '}
          <a href={`/app/sitter/${sitter.id}/calendar`} className='underline'>
            {t('pages.sitterProfile.modals.jobSeekingStatusModal.viewCalendar')}
          </a>
          .
        </p>
      </Modal>
    );
  }

  const descRemaining = STATUS_DESCRIPTION_MAX - description.length;
  const interviewRemaining = STATUS_DESCRIPTION_MAX - interviewTimes.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.jobSeekingStatus')}
      className='w-full max-w-3xl'
    >
      <h3 className='mb-3 font-semibold'>
        {t('pages.sitterProfile.modals.jobSeekingStatusModal.whatTypeHeading')}
      </h3>

      <div className='border border-neutral-200 rounded-lg p-4 mb-4 bg-neutral-50'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <Checkbox
            id='occasional-jobs'
            checked={occasional}
            onChange={(e): void => setOccasional(e.target.checked)}
            label={t('pages.sitterProfile.modals.jobSeekingStatusModal.occasionalJobs')}
          />
          <div>
            <h5 className='mb-1 font-semibold'>
              {t('pages.sitterProfile.modals.jobSeekingStatusModal.occasionalAvailabilityHeading')}
            </h5>
            <div>
              <i className='fa-regular fa-calendar text-sapphire mr-1' />
              {t('pages.sitterProfile.modals.jobSeekingStatusModal.keepCalendarCurrent')}
            </div>
            {lastCalendarUpdateLabel && (
              <div className='italic text-sm text-neutral-600'>
                {t('pages.sitterProfile.modals.jobSeekingStatusModal.lastUpdated', {
                  label: lastCalendarUpdateLabel,
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='border border-neutral-200 rounded-lg p-4 mb-4 bg-neutral-50'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <Checkbox
              id='part-time-jobs'
              className='mb-3'
              checked={partTime}
              onChange={(e): void => setPartTime(e.target.checked)}
              label={t('pages.sitterProfile.modals.jobSeekingStatusModal.partTimeJobs')}
            />
            <Checkbox
              id='full-time-jobs'
              className='mb-3'
              checked={fullTime}
              onChange={(e): void => setFullTime(e.target.checked)}
              label={t('pages.sitterProfile.modals.jobSeekingStatusModal.fullTimeJobs')}
            />
            <h5 className='font-semibold mb-0'>
              {t('pages.sitterProfile.modals.jobSeekingStatusModal.moreDetailsHeading')}
            </h5>
            <div className='text-right text-xs italic text-neutral-500 mb-1'>
              {t('pages.sitterProfile.modals.jobSeekingStatusModal.charactersRemaining', {
                count: descRemaining,
              })}
            </div>
            <textarea
              aria-label={t('pages.sitterProfile.modals.availabilityDescAriaLabel')}
              className='form-control w-full border border-neutral-300 rounded p-2'
              rows={3}
              maxLength={STATUS_DESCRIPTION_MAX}
              value={description}
              onChange={(e): void => setDescription(e.target.value)}
              placeholder={t('pages.sitterProfile.modals.availabilityDescPlaceholder')}
            />
          </div>
          <div>
            <h5 className='font-semibold mb-1'>
              {t('pages.sitterProfile.modals.jobSeekingStatusModal.snapshotHeading')}
            </h5>
            <p className='text-sm mb-2'>
              {t('pages.sitterProfile.modals.jobSeekingStatusModal.snapshotBodyPrefix')}{' '}
              <a href={`/app/sitter/${sitter.id}/calendar`} className='underline'>
                {t('pages.sitterProfile.modals.jobSeekingStatusModal.snapshotBodyLink')}
              </a>
              {t('pages.sitterProfile.modals.jobSeekingStatusModal.snapshotBodySuffix')}
            </p>
            <AvailabilityRecurringEditor
              days={recurringAvailability}
              onToggle={toggleRecurring}
            />
          </div>
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <div>
          <h5 className='font-semibold mb-0'>
            {t('pages.sitterProfile.modals.jobSeekingStatusModal.interviewHeading')}
          </h5>
          <div className='text-sm mb-1'>
            {t('pages.sitterProfile.modals.jobSeekingStatusModal.interviewBody')}
          </div>
          <div className='text-right text-xs italic text-neutral-500 mb-1'>
            {t('pages.sitterProfile.modals.jobSeekingStatusModal.charactersRemaining', {
              count: interviewRemaining,
            })}
          </div>
          <textarea
            aria-label={t('pages.sitterProfile.modals.interviewTimesAriaLabel')}
            className='form-control w-full border border-neutral-300 rounded p-2'
            rows={3}
            maxLength={STATUS_DESCRIPTION_MAX}
            value={interviewTimes}
            onChange={(e): void => setInterviewTimes(e.target.value)}
            placeholder={t('pages.sitterProfile.modals.interviewTimesPlaceholder')}
          />
        </div>
        <div className='text-center self-center'>
          <div className='mb-3'>
            <b>{t('pages.sitterProfile.modals.jobSeekingStatusModal.takingBreakHeading')}</b>{' '}
            {t('pages.sitterProfile.modals.jobSeekingStatusModal.takingBreakBody')}
          </div>
          <Button
            id='availability-status-modal-save'
            buttonType={ButtonType.primary}
            className='btn-block w-full'
            disabled={saving}
            onClick={save}
          >
            {saving ? (
              <>
                <i className='fa-solid fa-spinner fa-spin mr-2' />{' '}
                {t('pages.sitterProfile.modals.jobSeekingStatusModal.loading')}
              </>
            ) : (
              t('pages.sitterProfile.modals.jobSeekingStatusModal.update')
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
